import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import ReactGa from "react-ga";
import { BsInstagram } from "react-icons/bs";
import { FaFacebookF } from "react-icons/fa";
import Container from "./styled";
import FooterSimple from "../../components/footer/simple";
import url from "../../utils/url";
import { formatter } from "../../utils/formatter";

const VALOR_MINIMO = 100000;
const VALOR_MAXIMO = 7800000;
const PASO = 5000;

const formatoPesos = (valor) => `$${formatter.format(valor)}`;

// Código de área sin 0 y número sin 15: tienen que sumar 10 dígitos.
const normalizarTelefono = (codigoArea, telefono) => {
  const prefijo = codigoArea.replace(/\D/g, "").replace(/^0+/, "");
  let numero = telefono.replace(/\D/g, "");
  if (prefijo.length + numero.length === 12 && numero.startsWith("15")) {
    numero = numero.slice(2);
  }
  return { prefijo, numero };
};

const validar = ({ nombre, codigoArea, telefono }) => {
  const errores = {};
  const nombreLimpio = nombre.trim();
  if (nombreLimpio.length < 3 || !/^[a-zA-ZÀ-ÿñÑ' .-]+$/.test(nombreLimpio)) {
    errores.nombre = "Ingresá tu nombre y apellido.";
  }
  const { prefijo, numero } = normalizarTelefono(codigoArea, telefono);
  if (!prefijo || !numero) {
    errores.telefono = "Ingresá tu código de área y teléfono.";
  } else if (prefijo.length < 2 || prefijo.length > 4 || prefijo.length + numero.length !== 10) {
    errores.telefono =
      "Revisá el número: código de área sin 0 y teléfono sin 15 (10 dígitos en total).";
  }
  return errores;
};

export default function LandingBicicleteria({ utm, nombre: nombreBicicleteria, logo }) {
  const [searchParams] = useSearchParams();
  const [valores, setValores] = useState({ nombre: "", codigoArea: "", telefono: "", empresa: "" });
  const [valorBici, setValorBici] = useState(VALOR_MINIMO);
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(false);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.title = `Seguro de bici | ${nombreBicicleteria} x SeguroBici`;
  }, [nombreBicicleteria]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limpio = name === "codigoArea" || name === "telefono" ? value.replace(/\D/g, "") : value;
    setValores((prev) => ({ ...prev, [name]: limpio }));
    const campoError = name === "codigoArea" ? "telefono" : name;
    if (errores[campoError]) setErrores((prev) => ({ ...prev, [campoError]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviando) return;
    const erroresForm = validar(valores);
    setErrores(erroresForm);
    if (Object.keys(erroresForm).length) return;

    const { prefijo, numero } = normalizarTelefono(valores.codigoArea, valores.telefono);
    setEnviando(true);
    setErrorEnvio(false);
    try {
      await axios.post(`${url}/api/leads_alianzas/bicicleteria`, {
        bicicleteria: utm,
        nombre: valores.nombre.trim(),
        prefijo,
        telefono: numero,
        valor: Number(valorBici),
        empresa: valores.empresa,
        origen: /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent)
          ? "mobile"
          : "web",
        utm_source: searchParams.get("utm_source") || "",
        utm_medium: searchParams.get("utm_medium") || "",
        utm_campaign: searchParams.get("utm_campaign") || "",
      });
      ReactGa.event({ category: "Cotizar", action: "Click", label: `landing-${utm}` });
      setEnviado(true);
    } catch (error) {
      setErrorEnvio(true);
    } finally {
      setEnviando(false);
    }
  };

  const porcentaje = ((valorBici - VALOR_MINIMO) / (VALOR_MAXIMO - VALOR_MINIMO)) * 100;

  return (
    <Container>
      <header className="landing-header">
        <div className="landing-header-contenido">
          <img
            className="logo-segurobici"
            alt="Logo de SeguroBici"
            src="/images/logos/gys/segurobici.svg"
          />
          <div className="landing-header-contacto">
            <a href="tel:08102202373">0810-220-2373</a>
            <a
              href="https://www.instagram.com/segurobici/"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram de SeguroBici"
            >
              <BsInstagram />
            </a>
            <a
              href="https://es-la.facebook.com/SeguroBici/"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook de SeguroBici"
            >
              <FaFacebookF />
            </a>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="hero-titulo">
          <img className="logo-alianza" src={logo} alt={`Logo de ${nombreBicicleteria}`} />
          <h1>¡Cotizá tu seguro!</h1>
        </div>

        <div className="tarjeta">
          {enviado ? (
            <div className="confirmacion" role="status">
              <img src="/images/icons/check.svg" alt="" />
              <p className="confirmacion-mensaje">
                Te estará llamando un asesor para brindarte más información sobre las
                distintas coberturas del seguro de Bici.
              </p>
              <p className="confirmacion-gracias">¡Muchas gracias por confiar en SeguroBici!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <input
                type="text"
                name="nombre"
                autoComplete="name"
                placeholder="Nombre y Apellido"
                aria-label="Nombre y Apellido"
                value={valores.nombre}
                onChange={handleChange}
                className={errores.nombre ? "input-error" : ""}
              />
              {errores.nombre && <p className="mensaje-error">{errores.nombre}</p>}

              <div className="fila-telefono">
                <input
                  type="tel"
                  inputMode="numeric"
                  name="codigoArea"
                  autoComplete="tel-area-code"
                  placeholder="Cód. Área"
                  aria-label="Código de área"
                  maxLength={5}
                  value={valores.codigoArea}
                  onChange={handleChange}
                  className={errores.telefono ? "input-error" : ""}
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  name="telefono"
                  autoComplete="tel-local"
                  placeholder="Teléfono"
                  aria-label="Teléfono"
                  maxLength={10}
                  value={valores.telefono}
                  onChange={handleChange}
                  className={errores.telefono ? "input-error" : ""}
                />
              </div>
              {errores.telefono && <p className="mensaje-error">{errores.telefono}</p>}

              {/* Honeypot: oculto para personas, los bots lo completan */}
              <input
                type="text"
                name="empresa"
                className="campo-oculto"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={valores.empresa}
                onChange={handleChange}
              />

              <div className="valor-bici">
                <label htmlFor="valor-bici">Valor de la bicicleta</label>
                <span className="valor-bici-actual">{formatoPesos(valorBici)}</span>
                <input
                  id="valor-bici"
                  type="range"
                  min={VALOR_MINIMO}
                  max={VALOR_MAXIMO}
                  step={PASO}
                  value={valorBici}
                  onChange={(e) => setValorBici(Number(e.target.value))}
                  style={{ "--relleno": `${porcentaje}%` }}
                />
                <span className="valor-bici-limite">{formatoPesos(VALOR_MINIMO)}</span>
                <span className="valor-bici-limite">{formatoPesos(VALOR_MAXIMO)}</span>
              </div>

              {errorEnvio && (
                <p className="mensaje-error mensaje-envio">
                  No pudimos enviar tus datos. Probá de nuevo en unos minutos.
                </p>
              )}

              <button type="submit" disabled={enviando}>
                {enviando ? "Enviando..." : "¡Cotizá ahora!"}
                <img alt="" src="/images/home/cotizador/iconcotizador.svg" />
              </button>
            </form>
          )}
        </div>
      </section>

      <FooterSimple />
    </Container>
  );
}
